const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'a',
  'img',
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
};

const isSafeUrl = (value: string) => {
  const trimmed = value.trim();

  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('data:image/') ||
    trimmed.startsWith('blob:')
  );
};

export const sanitizeNoteHtml = (html: string) => {
  if (!html || typeof window === 'undefined') {
    return html || '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const sanitizeNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.parentNode?.removeChild(node);
      return;
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tagName)) {
      if (tagName === 'script' || tagName === 'style' || tagName === 'iframe' || tagName === 'object') {
        element.remove();
        return;
      }

      const parent = element.parentNode;
      if (!parent) {
        element.remove();
        return;
      }

      while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
      }
      parent.removeChild(element);
      return;
    }

    Array.from(element.attributes).forEach(attr => {
      const attrName = attr.name.toLowerCase();
      const allowedAttrs = ALLOWED_ATTRS[tagName];
      const isAllowedAttr = allowedAttrs?.has(attrName) ?? false;

      if (!isAllowedAttr) {
        element.removeAttribute(attr.name);
      }
    });

    if (tagName === 'a') {
      const href = element.getAttribute('href');
      if (!href || !isSafeUrl(href)) {
        element.removeAttribute('href');
      } else {
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener noreferrer');
      }
    }

    if (tagName === 'img') {
      const src = element.getAttribute('src');
      if (!src || !isSafeUrl(src)) {
        element.remove();
        return;
      }
    }

    Array.from(element.childNodes).forEach(sanitizeNode);
  };

  Array.from(doc.body.childNodes).forEach(sanitizeNode);

  return doc.body.innerHTML;
};
